import { StaticImage } from "gatsby-plugin-image"
import React, { useEffect, useRef, useState } from "react"
import Layout from "../components/layout"
import ContentDiv from "../components/contentDiv"
import PageImage from "../components/pageImage"
import SEO from "../components/seo"
import { Media } from "gatsby-plugin-fresnel"
import FirstSection from "../components/firstSection"

/**
 * Filtering System Documentation (based on erv.js):
 * 
 * The filtering system uses the erv.js script from https://107802.mainfo.net/erv.js
 * 
 * Key functions:
 * - ervmonatsaufruf(page, month) -> calls ar(page, "mo=" + month)
 *   Uses parameter: ?mo=0 (not ?month=0)
 * 
 * - ervbeitragsaufruf(page, id) -> calls ar(page, "ervid=" + id)
 *   Uses parameter: ?ervid=DW20251201 (not ?id=)
 * 
 * - ar(page, params) -> Makes AJAX request to fetch filtered content
 *   Makes request to: 'https://'+K+'.mainfo.net/'+page+'?'+params
 *   Updates document.getElementById('myContent').innerHTML with response
 *   When params is empty (''), loads the page with empty query string
 * 
 * - Category links: ar('10alle.html', '') -> Loads category page via AJAX
 *   Category pages like 10alle.html, 11gmbh.html are pre-filtered HTML pages
 * 
 * The filtering works via AJAX - the ar() function fetches filtered HTML content
 * and injects it into a div with id="myContent" on the current page.
 * When opening in a new window, we fetch the filtered content server-side
 * using the correct parameter names.
 */

// Server-side data fetching function
export async function getSteuernewsData() {
  try {
    // Fetch the actual HTML content directly from aktuelles.html
    // This bypasses the JavaScript loader (erv.js) and gets the content directly
    const response = await fetch('https://107802.mainfo.net/aktuelles.html');

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const htmlContent = await response.text();
    
    // Return the actual HTML content with styles
    return {
      success: true,
      content: htmlContent
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      content: `
        <div style="text-align: center; padding: 20px; color: #666;">
          <h3>Steuernews</h3>
          <p>Die aktuellen Steuernews konnten nicht geladen werden.</p>
          <p>Bitte versuchen Sie es später erneut.</p>
        </div>
      `
    };
  }
}

const Steuernews = () => {
    const [content, setContent] = useState<string>('')
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [error, setError] = useState<string>('')
    const contentRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const fetchContent = async () => {
            try {
                setIsLoading(true)
                
                // Use the server-side function directly
                const result = await getSteuernewsData()
                
                if (result.success) {
                    setContent(result.content)
                    setError('')
                } else {
                    setError(result.error || 'Unknown error occurred')
                }
            } catch (err) {
                setError(`Failed to load content: ${err instanceof Error ? err.message : 'Unknown error'}`)
            } finally {
                setIsLoading(false)
            }
        }

        fetchContent()
    }, [])

    // Helper function to parse JavaScript function calls from href
    const parseJavaScriptHref = (href: string | null): { functionName: string; params: string[] } | null => {
        if (!href || !href.startsWith('javascript:')) {
            return null;
        }
        
        // Remove 'javascript:' prefix and 'void(' wrapper
        const jsCode = href.replace(/^javascript:void\(/, '').replace(/\)$/, '');
        
        // Match function calls like: ervbeitragsaufruf('beitrag.html','DW20251201')
        const match = jsCode.match(/^(\w+)\((.*)\)$/);
        if (!match) {
            return null;
        }
        
        const functionName = match[1];
        const paramsString = match[2];
        
        // Parse parameters (simple string parsing for quoted strings)
        const params: string[] = [];
        let currentParam = '';
        let inQuotes = false;
        let quoteChar = '';
        
        for (let i = 0; i < paramsString.length; i++) {
            const char = paramsString[i];
            
            if ((char === '"' || char === "'") && (i === 0 || paramsString[i - 1] !== '\\')) {
                if (!inQuotes) {
                    inQuotes = true;
                    quoteChar = char;
                } else if (char === quoteChar) {
                    inQuotes = false;
                    params.push(currentParam);
                    currentParam = '';
                    // Skip comma and whitespace
                    while (i + 1 < paramsString.length && (paramsString[i + 1] === ',' || paramsString[i + 1] === ' ')) {
                        i++;
                    }
                }
            } else if (inQuotes) {
                currentParam += char;
            }
        }
        
        return { functionName, params };
    }

    // Effect to load and execute scripts, and handle interactions after content is rendered
    useEffect(() => {
        if (!content || !contentRef.current) {
            return;
        }

        const container = contentRef.current;
        
        // Extract and execute script tags from the HTML
        const scripts = container.querySelectorAll('script');
        const loadedScripts: HTMLScriptElement[] = [];
        
        scripts.forEach((oldScript) => {
            const newScript = document.createElement('script');
            
            // Copy attributes
            Array.from(oldScript.attributes).forEach((attr) => {
                newScript.setAttribute(attr.name, attr.value);
            });
            
            // Copy content if inline script
            if (oldScript.textContent) {
                newScript.textContent = oldScript.textContent;
            }
            
            // Handle src attribute - load external scripts
            if (oldScript.src) {
                const src = oldScript.src.startsWith('http') 
                    ? oldScript.src 
                    : `https://107802.mainfo.net/${oldScript.src}`;
                newScript.src = src;
            }
            
            // Replace old script with new one and execute
            oldScript.parentNode?.replaceChild(newScript, oldScript);
            loadedScripts.push(newScript);
        });
        
        // Define global functions that scripts might call (only if they don't exist)
        if (typeof window !== 'undefined') {
            // Define ervmonatsaufruf function for month filtering
            // According to erv.js: ervmonatsaufruf(a,b) calls ar(a,"mo="+b)
            if (typeof (window as any).ervmonatsaufruf === 'undefined') {
                (window as any).ervmonatsaufruf = (page: string, month: string) => {
                    const monthUrl = `https://107802.mainfo.net/${page}?mo=${month}`;
                    fetch(monthUrl)
                        .then(response => response.text())
                        .then(html => {
                            const newWindow = window.open('', '_blank');
                            if (newWindow) {
                                newWindow.document.write(html);
                                newWindow.document.close();
                            }
                        })
                        .catch(() => {
                            window.open(monthUrl, '_blank');
                        });
                };
            }
            
            // Define ar function for category filtering
            // According to erv.js: ar(a,b) makes AJAX request to fetch content
            // When opening in new window, fetch and inject the content
            if (typeof (window as any).ar === 'undefined') {
                (window as any).ar = (categoryPage: string, queryParams?: string) => {
                    const categoryUrl = `https://107802.mainfo.net/${categoryPage}${queryParams ? '?' + queryParams : ''}`;
                    fetch(categoryUrl)
                        .then(response => response.text())
                        .then(html => {
                            const newWindow = window.open('', '_blank');
                            if (newWindow) {
                                newWindow.document.write(html);
                                newWindow.document.close();
                            }
                        })
                        .catch(() => {
                            window.open(categoryUrl, '_blank');
                        });
                };
            }
            
            // Define ervbeitragsaufruf function for article links
            // According to erv.js: ervbeitragsaufruf(a,b) calls ar(a,"ervid="+b)
            if (typeof (window as any).ervbeitragsaufruf === 'undefined') {
                (window as any).ervbeitragsaufruf = (page: string, id: string) => {
                    const articleUrl = `https://107802.mainfo.net/${page}?ervid=${id}`;
                    window.open(articleUrl, '_blank');
                };
            }
        }
        
        // Load erv.js if it's referenced but not already loaded
        if (typeof window !== 'undefined' && !(window as any).erv) {
            const ervScript = document.createElement('script');
            ervScript.src = 'https://107802.mainfo.net/erv.js';
            ervScript.async = false; // Load synchronously to ensure it's available
            ervScript.onload = () => {
                // Script loaded - trigger any initialization if needed
                if (typeof (window as any).erv === 'function') {
                    try {
                        (window as any).erv();
                    } catch (err) {
                        // Ignore initialization errors
                    }
                }
            };
            document.head.appendChild(ervScript);
        }
        
        // Handle links - but allow filter links to work natively
        const links = container.querySelectorAll('a');
        const clickHandlers: Array<{ element: Element; handler: (e: Event) => void }> = [];
        
        links.forEach((link) => {
            const href = link.getAttribute('href');
            const target = link.getAttribute('target');
            
            const clickHandler = (e: Event) => {
                e.preventDefault();
                e.stopPropagation();
                
                // Handle JavaScript links
                if (href && href.startsWith('javascript:')) {
                    const parsed = parseJavaScriptHref(href);
                    if (parsed) {
                        // Handle different function types
                        if (parsed.functionName === 'ervbeitragsaufruf' && parsed.params.length >= 2) {
                            // Article link: ervbeitragsaufruf('beitrag.html','DW20251201')
                            // According to erv.js: ervbeitragsaufruf(a,b) calls ar(a,"ervid="+b)
                            const articleUrl = `https://107802.mainfo.net/${parsed.params[0]}?ervid=${parsed.params[1]}`;
                            window.open(articleUrl, '_blank');
                        } else if (parsed.functionName === 'ervmonatsaufruf' && parsed.params.length >= 2) {
                            // Month link: ervmonatsaufruf('aktuelles.html','0')
                            // According to erv.js: ervmonatsaufruf calls ar(a,"mo="+b)
                            // So we need to use 'mo=' parameter, not 'month='
                            const monthParam = parsed.params[1];
                            const monthUrl = `https://107802.mainfo.net/${parsed.params[0]}?mo=${monthParam}`;
                            fetch(monthUrl)
                                .then(response => response.text())
                                .then(html => {
                                    // Open filtered content in new window
                                    const newWindow = window.open('', '_blank');
                                    if (newWindow) {
                                        newWindow.document.write(html);
                                        newWindow.document.close();
                                    }
                                })
                                .catch(() => {
                                    // Fallback to regular URL if fetch fails
                                    window.open(monthUrl, '_blank');
                                });
                        } else if (parsed.functionName === 'ar' && parsed.params.length >= 1) {
                            // Category link: ar('10alle.html','')
                            // According to erv.js: ar(a,b) makes AJAX request to 'https://'+K+'.mainfo.net/'+a+'?'+b
                            // When b is empty, it loads the category page directly
                            // Fetch the category page content and open it in a new window
                            const categoryPage = parsed.params[0];
                            const queryParams = parsed.params.length > 1 ? parsed.params[1] : '';
                            const categoryUrl = `https://107802.mainfo.net/${categoryPage}${queryParams ? '?' + queryParams : ''}`;
                            fetch(categoryUrl)
                                .then(response => response.text())
                                .then(html => {
                                    // Open filtered content in new window
                                    const newWindow = window.open('', '_blank');
                                    if (newWindow) {
                                        newWindow.document.write(html);
                                        newWindow.document.close();
                                    }
                                })
                                .catch(() => {
                                    // Fallback to regular URL if fetch fails
                                    window.open(categoryUrl, '_blank');
                                });
                        } else {
                            // Unknown JavaScript function - try to execute it
                            try {
                                eval(parsed.functionName + '(' + parsed.params.map(p => `"${p}"`).join(',') + ')');
                            } catch (err) {
                                // If execution fails, allow default behavior
                                return;
                            }
                        }
                    } else {
                        // Couldn't parse - try to extract function name and handle known functions
                        const jsCode = href.replace(/^javascript:/, '').replace(/^void\(/, '').replace(/\)$/, '');
                        
                        // Try to match function calls even if parsing failed
                        const match = jsCode.match(/^(ervmonatsaufruf|ar|ervbeitragsaufruf)\s*\(/);
                        if (match) {
                            const funcName = match[1];
                            // Try to extract parameters manually
                            const paramMatch = jsCode.match(/\(([^)]*)\)/);
                            if (paramMatch) {
                                const paramsStr = paramMatch[1];
                                // Simple parameter extraction - split by comma, remove quotes
                                const params = paramsStr.split(',').map(p => p.trim().replace(/^['"]|['"]$/g, '')).filter(p => p);
                                
                                if (funcName === 'ervmonatsaufruf' && params.length >= 2) {
                                    // According to erv.js: ervmonatsaufruf calls ar(a,"mo="+b)
                                    const monthParam = params[1];
                                    const monthUrl = `https://107802.mainfo.net/${params[0]}?mo=${monthParam}`;
                                    fetch(monthUrl)
                                        .then(response => response.text())
                                        .then(html => {
                                            const newWindow = window.open('', '_blank');
                                            if (newWindow) {
                                                newWindow.document.write(html);
                                                newWindow.document.close();
                                            }
                                        })
                                        .catch(() => {
                                            window.open(monthUrl, '_blank');
                                        });
                                    return;
                                } else if (funcName === 'ar' && params.length >= 1) {
                                    // According to erv.js: ar(a,b) makes AJAX request
                                    const categoryPage = params[0];
                                    const queryParams = params.length > 1 ? params[1] : '';
                                    const categoryUrl = `https://107802.mainfo.net/${categoryPage}${queryParams ? '?' + queryParams : ''}`;
                                    fetch(categoryUrl)
                                        .then(response => response.text())
                                        .then(html => {
                                            const newWindow = window.open('', '_blank');
                                            if (newWindow) {
                                                newWindow.document.write(html);
                                                newWindow.document.close();
                                            }
                                        })
                                        .catch(() => {
                                            window.open(categoryUrl, '_blank');
                                        });
                                    return;
                                } else if (funcName === 'ervbeitragsaufruf' && params.length >= 2) {
                                    // According to erv.js: ervbeitragsaufruf calls ar(a,"ervid="+b)
                                    const articleUrl = `https://107802.mainfo.net/${params[0]}?ervid=${params[1]}`;
                                    window.open(articleUrl, '_blank');
                                    return;
                                }
                            }
                        }
                        
                        // If we can't handle it, don't try to execute (functions don't exist)
                        console.warn('Could not handle JavaScript link:', href);
                    }
                } else if (href && (href.startsWith('http://') || href.startsWith('https://'))) {
                    // Regular HTTP link
                    if (target === '_blank') {
                        window.open(href, '_blank');
                    } else {
                        window.location.href = href;
                    }
                } else if (href) {
                    // Relative link
                    const fullUrl = `https://107802.mainfo.net/${href}`;
                    window.open(fullUrl, '_blank');
                }
            };
            
            link.addEventListener('click', clickHandler, true);
            clickHandlers.push({ element: link, handler: clickHandler });
        });
        
        // Handle search form submission
        const searchForm = container.querySelector('form#erv-archiv-searchbar') as HTMLFormElement;
        if (searchForm) {
            const formHandler = (e: Event) => {
                e.preventDefault();
                e.stopPropagation();
                
                const form = e.currentTarget as HTMLFormElement;
                const searchInput = form.querySelector('input[name="such"]') as HTMLInputElement;
                const searchTerm = searchInput?.value;
                
                if (searchTerm && searchTerm !== 'Suchbegriff eingeben...') {
                    // Fetch filtered content
                    const searchUrl = `https://107802.mainfo.net/aktuelles.html?such=${encodeURIComponent(searchTerm)}`;
                    fetch(searchUrl)
                        .then(response => response.text())
                        .then(html => {
                            // Update content state which will trigger re-initialization
                            setContent(html);
                        })
                        .catch(() => {
                            // Error handling - could show error message
                        });
                }
            };
            
            searchForm.addEventListener('submit', formHandler);
        }
        
        // Handle form inputs and checkboxes for filter functionality
        const inputs = container.querySelectorAll('input, select, textarea');
        inputs.forEach((input) => {
            // Let native events work - don't interfere with filter functionality
            // Only handle the search input placeholder
            if (input.id === 'erv-search' || input.classList.contains('erv-archiv-suchfeld')) {
                input.addEventListener('focus', () => {
                    if ((input as HTMLInputElement).value === 'Suchbegriff eingeben...') {
                        (input as HTMLInputElement).value = '';
                    }
                });
                input.addEventListener('blur', () => {
                    if ((input as HTMLInputElement).value === '') {
                        (input as HTMLInputElement).value = 'Suchbegriff eingeben...';
                    }
                });
            }
        });
        
        // Handle label clicks (for responsive nav icon)
        const labels = container.querySelectorAll('label');
        labels.forEach((label) => {
            const htmlFor = label.getAttribute('for');
            if (htmlFor) {
                const targetElement = container.querySelector(`#${htmlFor}`) || document.getElementById(htmlFor);
                if (targetElement) {
                    label.addEventListener('click', () => {
                        // Trigger click on the associated input/button
                        if (targetElement instanceof HTMLInputElement || targetElement instanceof HTMLButtonElement) {
                            targetElement.click();
                        }
                    });
                }
            }
        });
        
        // Cleanup
        return () => {
            clickHandlers.forEach(({ element, handler }) => {
                element.removeEventListener('click', handler, true);
            });
        };
    }, [content])

    return (
        <Layout>
            <PageImage>

                <Media greaterThanOrEqual="md">
                    <StaticImage


                        src="../images/Titelbild_Steuernews_PC.jpeg"
                        quality={95}
                        formats={["auto", "webp", "avif"]}
                        alt="Image"
                        objectFit="contain"
                        layout="fullWidth"
                    />
                </Media>
                <Media at="sm">
                    <StaticImage


                        src="../images/Titelbild_Steuernews_Smartp.jpeg"
                        quality={95}
                        formats={["auto", "webp", "avif"]}
                        alt="Image"
                        objectFit="contain"
                        layout="fullWidth"
                    />
                </Media>
            </PageImage>

            <FirstSection headerText="Steuernews" >
                Immer aktuell mit unseren Steuernews:
                Erfahren Sie laufend aktuelle Informationen sowie steuerliche und gesetzliche Neuerungen!
            </FirstSection>
            <ContentDiv>
                {/* Display content */}
                {error ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: 'red' }}>
                        <p>Error: {error}</p>
                        <button 
                            onClick={() => window.location.reload()} 
                            style={{ 
                                padding: '10px 20px', 
                                marginTop: '10px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '5px',
                                cursor: 'pointer'
                            }}
                        >
                            Retry
                        </button>
                    </div>
                ) : content ? (
                    <div 
                        ref={contentRef}
                        dangerouslySetInnerHTML={{ __html: content }}
                    />
                ) : (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <p>{isLoading ? 'Loading news content...' : 'No content available'}</p>
                        {isLoading && (
                            <div style={{ marginTop: '20px' }}>
                                <div style={{ 
                                    width: '40px', 
                                    height: '40px', 
                                    border: '4px solid #f3f3f3', 
                                    borderTop: '4px solid #007bff', 
                                    borderRadius: '50%', 
                                    animation: 'spin 1s linear infinite',
                                    margin: '0 auto'
                                }} />
                                <style>{`
                                    @keyframes spin {
                                        0% { transform: rotate(0deg); }
                                        100% { transform: rotate(360deg); }
                                    }
                                `}</style>
                            </div>
                        )}
                    </div>
                )}
            </ContentDiv>
        </Layout>)

}

// Gatsby Head API - modern way to manage document head
export const Head = () => {
  const seoData = SEO({ lang: "de", title: "Steuernews" })
  
  return (
    <>
      <html lang={seoData.lang} />
      <title>{seoData.titleTemplate ? seoData.titleTemplate.replace('%s', seoData.title) : seoData.title}</title>
      {seoData.meta.map((meta, index) => (
        <meta key={index} name={meta.name} property={meta.property} content={meta.content} />
      ))}
    </>
  )
}

export default Steuernews
